import React from 'react';
import { Button, Checkbox, Input, Textarea, makeStyles, shorthands } from '@fluentui/react-components';
import { customColors, customFonts } from '../../../../../styles';
import type { UsePersonaVm } from '../../../hooks/usePersona';

const useStyles = makeStyles({
    personaTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    personaLibraryButton: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '600',
        minWidth: '150px',
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        color: customColors.protivitiDarkBlue,
    },
    personaTitleInput: {
        fontFamily: customFonts.Lato,
    },
    personaInput: {
        minHeight: '130px',
        fontFamily: customFonts.Lato,
    },
    personaActions: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
    },
    personaCancelButton: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '600',
        minWidth: '130px',
    },
    personaSetButton: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '600',
        minWidth: '130px',
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        backgroundColor: customColors.protivitiOrange,
        color: customColors.white,
    },
    personaEditingHint: {
        fontFamily: customFonts.Lato,
        fontSize: '13px',
        fontWeight: '700',
        color: customColors.protivitiOrange,
    },
});

interface PersonaEditorViewProps {
    vm: UsePersonaVm;
}

const PersonaEditorView: React.FC<PersonaEditorViewProps> = ({ vm }) => {
    const styles = useStyles();

    return (
        <>
            <div className={styles.personaTopRow}>
                <Checkbox
                    label="Save this persona to your library."
                    checked={vm.shouldSavePersona}
                    onChange={(_, data) => vm.setShouldSavePersona(Boolean(data.checked))}
                    disabled={vm.isPersonaSaving}
                />
                <Button
                    className={styles.personaLibraryButton}
                    appearance="transparent"
                    onClick={vm.openPersonaLibrary}
                >
                    Persona Library
                </Button>
            </div>
            {vm.editingSavedPersonaId && <div className={styles.personaEditingHint}>Editing saved persona</div>}
            {vm.shouldSavePersona && (
                <Input
                    className={styles.personaTitleInput}
                    placeholder="Enter a title for this persona..."
                    value={vm.personaTitle}
                    onChange={(_, data) => vm.setPersonaTitle(data.value)}
                    disabled={vm.isPersonaSaving}
                />
            )}
            <Textarea
                className={styles.personaInput}
                placeholder="Start typing a persona here..."
                value={vm.personaValue}
                onChange={(_, data) => vm.setPersonaValue(data.value)}
            />
            <div className={styles.personaActions}>
                <Button
                    className={styles.personaCancelButton}
                    appearance="secondary"
                    onClick={vm.closePersonaDialog}
                    disabled={vm.isPersonaSaving}
                >
                    Cancel
                </Button>
                <Button
                    className={styles.personaSetButton}
                    appearance="primary"
                    onClick={() => void vm.savePersona()}
                    disabled={
                        vm.isPersonaSaving ||
                        !vm.personaValue.trim() ||
                        (vm.shouldSavePersona && !vm.personaTitle.trim())
                    }
                >
                    {vm.isPersonaSaving ? 'Saving...' : 'Set Persona'}
                </Button>
            </div>
        </>
    );
};

export default PersonaEditorView;
